
import { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { canAccessFeature, type RoleName } from '@/lib/utils/role-access';
import {
    SlidersHorizontal,
    Calendar as CalendarIcon,
    Eye,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Clock
} from 'lucide-react';
import { format } from 'date-fns';

interface WebhookLog {
    id: number;
    eventType: string;
    streamUid: string | null;
    payload: string;
    status: 'success' | 'error' | 'processing';
    errorMessage: string | null;
    receivedAt: string;
    processedAt: string | null;
}

export function WebhookLogs() {
    const { user } = useAuthStore();
    const [logs, setLogs] = useState<WebhookLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalItems, setTotalItems] = useState(0);
    const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

    // Filters
    const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [streamUidFilter, setStreamUidFilter] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | undefined>();
    const [endDate, setEndDate] = useState<Date | undefined>();
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const fetchLogs = useCallback(async () => {
        try {
            setIsLoading(true);

            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
            });

            if (eventTypeFilter && eventTypeFilter !== 'all') params.append('eventType', eventTypeFilter);
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
            if (streamUidFilter) params.append('streamUid', streamUidFilter);
            if (startDate) params.append('startDate', startDate.toISOString());
            if (endDate) params.append('endDate', endDate.toISOString());

            const response = await api.get<{ logs: any[]; pagination: { total: number } }>(`api/webhooks/logs?${params}`);

            if (response.success && response.data?.logs) {
                setLogs(response.data.logs);
                setTotalItems(response.data.pagination.total);
            } else {
                toast.error('Failed to load webhook logs');
            }
        } catch (error) {
            console.error('Failed to load webhook logs:', error);
            toast.error('Failed to load webhook logs');
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, eventTypeFilter, statusFilter, streamUidFilter, startDate, endDate]);

    useEffect(() => {
        if (canAccessFeature('view_analytics', user?.role?.name as RoleName)) {
            fetchLogs();
        }
    }, [user, fetchLogs]);

    const handleRefresh = () => {
        fetchLogs();
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'success':
                return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Success</Badge>;
            case 'error':
                return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
            case 'processing':
                return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Processing</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const renderSkeletonRows = (count: number) => {
        return Array.from({ length: count }).map((_, index) => (
            <TableRow key={`skeleton-${index}`}>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-8 w-8 rounded ml-auto" /></TableCell>
            </TableRow>
        ));
    };

    const formatPayload = (payload: string) => {
        try {
            return JSON.stringify(JSON.parse(payload), null, 2);
        } catch {
            return payload;
        }
    };

    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <div className="space-y-6">
            {/* Filters */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <SlidersHorizontal className="h-5 w-5" />
                        Filters
                    </CardTitle>
                    <CardDescription>
                        Filter webhook logs by event type, status, stream UID, and date range
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="mt-1 flex items-center justify-between gap-2">
                        <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
                            <SheetTrigger asChild>
                                <Button variant="outline" size="sm">
                                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                                    Filters
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[min(92vw,420px)] p-0">
                                <SheetHeader className="border-b p-4 pr-12">
                                    <SheetTitle>Webhook Log Filters</SheetTitle>
                                    <SheetDescription>Filter by event type, status, stream UID, and date range.</SheetDescription>
                                </SheetHeader>
                                <div className="space-y-4 p-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="eventType">Event Type</Label>
                                        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="All events" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All events</SelectItem>
                                                <SelectItem value="stream.live">Stream Live</SelectItem>
                                                <SelectItem value="stream.ended">Stream Ended</SelectItem>
                                                <SelectItem value="recording.ready">Recording Ready</SelectItem>
                                                <SelectItem value="stream.recording">Stream Recording</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="status">Status</Label>
                                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="All statuses" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All statuses</SelectItem>
                                                <SelectItem value="success">Success</SelectItem>
                                                <SelectItem value="error">Error</SelectItem>
                                                <SelectItem value="processing">Processing</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="streamUid">Stream UID</Label>
                                        <Input
                                            id="streamUid"
                                            placeholder="Enter stream UID"
                                            value={streamUidFilter}
                                            onChange={(e) => setStreamUidFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Start Date</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar
                                                    mode="single"
                                                    selected={startDate}
                                                    onSelect={setStartDate}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>End Date</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar
                                                    mode="single"
                                                    selected={endDate}
                                                    onSelect={setEndDate}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                        {(eventTypeFilter !== 'all' || statusFilter !== 'all' || streamUidFilter || startDate || endDate) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setEventTypeFilter('all');
                                    setStatusFilter('all');
                                    setStreamUidFilter('');
                                    setStartDate(undefined);
                                    setEndDate(undefined);
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </div>

                    <div className="mt-4 flex gap-2">
                        <Button onClick={handleRefresh} variant="outline" size="icon">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Logs Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Webhook Events</CardTitle>
                    <CardDescription>
                        {totalItems} total events • Page {currentPage} of {totalPages}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Timestamp</TableHead>
                                    <TableHead>Event Type</TableHead>
                                    <TableHead>Stream UID</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Processed At</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? renderSkeletonRows(pageSize) : logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell className="font-mono text-sm">
                                            {new Date(log.receivedAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{log.eventType}</Badge>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            {log.streamUid || 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(log.status)}
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            {log.processedAt ? new Date(log.processedAt).toLocaleString() : 'N/A'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSelectedLog(log)}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <DataTablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }}
                    />
                </CardContent>
            </Card>

            <Dialog
                open={selectedLog !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedLog(null);
                }}
            >
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    {selectedLog && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Webhook Event Details</DialogTitle>
                                <DialogDescription>
                                    Event ID: {selectedLog.id} • Received: {new Date(selectedLog.receivedAt).toLocaleString()}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Event Type</Label>
                                        <p className="font-mono">{selectedLog.eventType}</p>
                                    </div>
                                    <div>
                                        <Label>Status</Label>
                                        <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                                    </div>
                                    <div>
                                        <Label>Stream UID</Label>
                                        <p className="font-mono">{selectedLog.streamUid || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <Label>Processed At</Label>
                                        <p className="font-mono">
                                            {selectedLog.processedAt ? new Date(selectedLog.processedAt).toLocaleString() : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                {selectedLog.errorMessage && (
                                    <div>
                                        <Label>Error Message</Label>
                                        <p className="text-red-600 font-mono text-sm bg-red-50 p-2 rounded">
                                            {selectedLog.errorMessage}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <Label>Payload</Label>
                                    <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono">
                                        {formatPayload(selectedLog.payload)}
                                    </pre>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
